/**
 * @fileoverview 特集カードコンポーネントの汎用的な列レイアウトコンポーネント
 */
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { cardCommonProps } from '@/components/_parials/contentsCard'
import StoryCard from '@/components/contents/StoryCard.vue'

export default defineComponent({
  name: 'LayoutStoryCardsCols',

  components: {
    StoryCard,
  },

  props: {
    ...cardCommonProps,

    /**
     * コンテンツ情報の配列
     */
    items: {
      type: Array as PropType<StoryItem[]>,
      required: true,
    },

    /**
     * 表示する列数
     * 指定範囲: 1 〜 6
     * @description 'fixed' を指定した場合は、カラム一つひとつの幅を固定化する
     */
    cols: {
      type: Object as PropType<{
        pc: 1 | 2 | 3 | 4 | 5 | 6 | 'fixed',
        sp: 1 | 2 | 3 | 4 | 5 | 6 | 'fixed',
      }>,
      default: () => ({
        pc: 3,
        sp: 1,
      }),
      validator(value: { pc: number | string, sp: number | string }) {
        // 1 〜 6 の整数値が指定されてるかどうか
        const pc = value.pc === 'fixed' || (typeof value.pc === 'number' && [1,2,3,4,5,6].includes(value.pc))
        const sp = value.sp === 'fixed' || (typeof value.sp === 'number' && [1,2,3,4,5,6].includes(value.sp))

        return pc && sp
      },
    },

    /**
     * PC でも横向きタイプ表示にするフラグ
     * @description 重複定義の場合は full の設定が優先される
     */
    horizontal: {
      type: Object as PropType<{ pc: boolean, sp: boolean }>,
      default: () => ({
        pc: false,
        sp: false,
      }),
    },

    /**
     * 特集のテーマ（特集カテゴリー）名を表示するかどうか
     */
    hasTheme: {
      type: Boolean,
      default: false,
    },

    /**
     * 特集に紐づくレポート掲載号数を表示するかどうか
     */
    hasVol: {
      type: Boolean,
      default: false,
    },

    /**
     * 特集に紐づく item（レポート + 記事）総数を表示するかどうか
     */
    hasItemsTotal: {
      type: Boolean,
      default: false,
    },
  },
})
</script>

<template>
  <div
    v-if="items.length"
    class="o-list__storyCard"
    :data-cols-pc="cols.pc"
    :data-cols-sp="cols.sp"
    :style="{
      '--width-pc': imageOptions.width?.pc != null ? `${imageOptions.width?.pc}px` : '0',
      '--width-sp': imageOptions.width?.sp != null ? `${imageOptions.width?.sp}px` : '0',
    }">
    <ul class="o-list__storyCard--items">
      <li
        v-for="(item) in items"
        :key="item.id"
        class="o-list__storyCard--item">
        <StoryCard
          :item="item"
          :has-hero="hasHero"
          :highlight-word="highlightWord"
          :size="size"
          :horizontal="horizontal"
          :heading-options="headingOptions"
          :image-options="imageOptions"
          :has-theme="hasTheme"
          :has-vol="hasVol"
          :has-items-total="hasItemsTotal" />
      </li>
    </ul>
  <!-- /.o-list__storyCard --></div>
</template>

<style lang="sass" scoped>
.o-list__storyCard
  $root: &
  $vertical-gap-px: 24
  $horizontal-gap-px: 16
  --width-pc: 0
  --width-sp: 0
  width: 100%
  overflow-x: auto

  &:first-child
    margin-top: 0

  & &--items
    display: flex

  // Phablet 以上のとき
  +breakpoint($max: false, $min: true)
    margin: changeSizePx2Em($vertical-gap-px) auto 0

    &:not([data-cols-pc="fixed"])
      #{$root}--item
        margin-top: changeSizePx2Em($vertical-gap-px)

    &[data-cols-pc="fixed"]
      #{$root}--items
        width: max-content
        min-width: 100%
        flex-wrap: nowrap
        gap: #{$horizontal-gap-px}px

      #{$root}--item
        width: var(--width-pc)
        flex: 0 0 var(--width-pc)

    @for $i from 1 through 6
      &[data-cols-pc="#{$i}"]
        $col-width: math.div(($global-contents-width-pc - $horizontal-gap-px * ($i - 1)), $i)
        #{$root}--items
          width: 100%
          flex-wrap: wrap

        #{$root}--item
          width: getRelativeValue($col-width, $global-contents-width-pc)
          margin-left: getRelativeValue($horizontal-gap-px, $global-contents-width-pc)

          &:nth-child(-n+#{$i})
            margin-top: 0

          &:nth-child(#{$i}n+1)
            margin-left: 0

  // Phablet 未満のとき
  +breakpoint
    $vertical-gap-px: 16
    $horizontal-gap-px: 12
    margin: changeSizePx2Em($vertical-gap-px) auto 0

    &:not([data-cols-sp="fixed"])
      #{$root}--item
        margin-top: changeSizePx2Em($vertical-gap-px)

    &[data-cols-sp="fixed"]
      #{$root}--items
        width: max-content
        min-width: 100%
        flex-wrap: nowrap
        gap: #{$horizontal-gap-px}px

      #{$root}--item
        width: var(--width-sp)
        flex: 0 0 var(--width-sp)

    @for $i from 1 through 6
      &[data-cols-sp="#{$i}"]
        $col-width: math.div(($global-contents-width-phablet - $horizontal-gap-px * ($i - 1)), $i)
        #{$root}--items
          width: 100%
          flex-wrap: wrap

        #{$root}--item
          width: getRelativeValue($col-width, $global-contents-width-phablet)
          margin-left: getRelativeValue($horizontal-gap-px, $global-contents-width-phablet)

          &:nth-child(-n+#{$i})
            margin-top: 0

          &:nth-child(#{$i}n+1)
            margin-left: 0
</style>
